/**
 * RelatedSidebar - Shows document links grouped by relation type
 *
 * Features:
 * - Links grouped by relation type (Parent, Children, Related, etc.)
 * - Issue/epic associations from document_relations (editable)
 * - Clickable doc titles for navigation
 * - Hover-visible delete buttons
 * - Empty state with "Add link" CTA
 */

import { useState } from 'react';
import { Link2, Plus, X, FileText, BookOpen, StickyNote, Layout, CircleDot } from 'lucide-react';
import type { DocumentLinkWithTarget, DocumentRelationType, DocumentType } from '../../api/documents';

interface RelatedSidebarProps {
  links: DocumentLinkWithTarget[];
  onDeleteLink: (relationId: string) => void;
  onAddLink: () => void;
  onLinkDocClick: (docId: string) => void;
  isDeleting?: boolean;
  onIssueClick?: (issueId: string) => void;
}

const RELATION_LABELS: Record<DocumentRelationType, string> = {
  parent: 'Parent',
  child: 'Children',
  related: 'Related',
  blocks: 'Blocks',
  blocked_by: 'Blocked By',
  implements: 'Implements',
  tests: 'Tests',
  linked: 'Linked',
};

const RELATION_ORDER: DocumentRelationType[] = [
  'parent', 'child', 'related', 'blocks', 'blocked_by', 'implements', 'tests', 'linked',
];

function DocTypeIcon({ docType, className }: { docType: DocumentType; className?: string }) {
  switch (docType) {
    case 'spec':
      return <BookOpen size={12} className={className} />;
    case 'guide':
      return <Layout size={12} className={className} />;
    case 'note':
      return <StickyNote size={12} className={className} />;
    default:
      return <FileText size={12} className={className} />;
  }
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-neutral-100 text-neutral-500',
  in_progress: 'bg-blue-50 text-blue-600',
  review: 'bg-amber-50 text-amber-600',
  published: 'bg-emerald-50 text-emerald-600',
  archived: 'bg-neutral-100 text-neutral-400',
};

export function RelatedSidebar({
  links,
  onDeleteLink,
  onAddLink,
  onLinkDocClick,
  isDeleting,
  onIssueClick,
}: RelatedSidebarProps) {
  // Separate doc links from entity links
  const docLinks = links.filter((l) => l.target_type === 'document');
  const issueLinks = links.filter((l) => l.target_type === 'issue');
  const epicLinks = links.filter((l) => l.target_type === 'epic');

  const hasDocLinks = docLinks.length > 0;
  const hasEntityLinks = issueLinks.length > 0 || epicLinks.length > 0;
  const hasLinks = hasDocLinks || hasEntityLinks;

  // Group doc links by relation type
  const groupedDocLinks = RELATION_ORDER
    .map((type) => ({
      type,
      label: RELATION_LABELS[type],
      items: docLinks.filter((l) => l.relation_type === type),
    }))
    .filter((group) => group.items.length > 0);

  if (!hasLinks) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
        <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
          <Link2 size={20} className="text-neutral-400" />
        </div>
        <p className="text-sm text-neutral-500 mb-1">No linked items</p>
        <p className="text-xs text-neutral-400 mb-3">
          Connect this document to related docs, issues, or epics
        </p>
        <button
          onClick={onAddLink}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-coral-600 bg-coral-50 hover:bg-coral-100 rounded-md transition-colors"
        >
          <Plus size={14} />
          Add link
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Grouped doc links */}
      {groupedDocLinks.map((group) => (
        <div key={group.type} className="px-3 pt-3">
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-1.5 px-1">
            {group.label} ({group.items.length})
          </h4>
          <ul className="space-y-0.5">
            {group.items.map((link) => (
              <LinkItem
                key={link.id}
                link={link}
                onDelete={() => onDeleteLink(link.id)}
                onClick={() => link.target_document && onLinkDocClick(link.target_document.id)}
                isDeleting={isDeleting}
              />
            ))}
          </ul>
        </div>
      ))}

      {/* Issue/Epic associations */}
      {hasEntityLinks && (
        <div className={`px-3 pt-3 pb-3 ${hasDocLinks ? 'mt-2 border-t border-neutral-100' : ''}`}>
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-1.5 px-1">
            Associations ({issueLinks.length + epicLinks.length})
          </h4>
          <ul className="space-y-0.5">
            {issueLinks.map((link) => (
              <EntityLinkItem
                key={link.id}
                type="issue"
                entityId={link.target_entity_id!}
                onDelete={() => onDeleteLink(link.id)}
                onClick={() => link.target_entity_id && onIssueClick?.(link.target_entity_id)}
                isDeleting={isDeleting}
              />
            ))}
            {epicLinks.map((link) => (
              <EntityLinkItem
                key={link.id}
                type="epic"
                entityId={link.target_entity_id!}
                onDelete={() => onDeleteLink(link.id)}
                isDeleting={isDeleting}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Add Link button */}
      <div className="px-3 pb-3 pt-2">
        <button
          onClick={onAddLink}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium
                   text-neutral-500 hover:text-coral-600 border border-dashed border-neutral-200 hover:border-coral-200
                   hover:bg-coral-50/50 rounded-md transition-colors"
        >
          <Plus size={14} />
          Add link
        </button>
      </div>
    </div>
  );
}

interface LinkItemProps {
  link: DocumentLinkWithTarget;
  onDelete: () => void;
  onClick: () => void;
  isDeleting?: boolean;
}

function LinkItem({ link, onDelete, onClick, isDeleting }: LinkItemProps) {
  const [hovered, setHovered] = useState(false);
  const doc = link.target_document;
  if (!doc) return null;
  const statusClass = STATUS_COLORS[doc.status] || STATUS_COLORS.draft;

  return (
    <li
      className="group relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-neutral-600
                 hover:bg-neutral-50 hover:text-neutral-900 rounded transition-colors text-left"
      >
        <DocTypeIcon docType={doc.doc_type} className="text-neutral-400 flex-shrink-0" />
        <span className="truncate flex-1">{doc.title}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${statusClass}`}>
          {doc.status.replace('_', ' ')}
        </span>
      </button>

      {/* Delete button (hover-visible) */}
      {hovered && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isDeleting}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-red-50 text-neutral-300 hover:text-red-500 transition-colors"
          title="Remove link"
        >
          <X size={12} />
        </button>
      )}
    </li>
  );
}

interface EntityLinkItemProps {
  type: 'issue' | 'epic';
  entityId: string;
  onDelete: () => void;
  onClick?: () => void;
  isDeleting?: boolean;
}

function EntityLinkItem({ type, entityId, onDelete, onClick, isDeleting }: EntityLinkItemProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <li
      className="group relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        onClick={onClick}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-neutral-600
                 hover:bg-neutral-50 hover:text-neutral-900 rounded transition-colors text-left"
      >
        {type === 'issue' ? (
          <CircleDot size={12} className="text-coral-500 flex-shrink-0" />
        ) : (
          <Layout size={12} className="text-neutral-400 flex-shrink-0" />
        )}
        <span className="truncate flex-1">
          {type === 'issue' ? `Issue: ${entityId}` : `Epic: ${entityId}`}
        </span>
      </button>

      {hovered && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isDeleting}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-red-50 text-neutral-300 hover:text-red-500 transition-colors"
          title="Remove link"
        >
          <X size={12} />
        </button>
      )}
    </li>
  );
}

export default RelatedSidebar;
